'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetWinners } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { ShareButton } from '@/components/market/share-button';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Panel } from '@/components/ui/panel';
import { D9_SCALE } from '@/config/solana';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { SignedBps } from './winner/signed-bps';
import { StatTile } from './winner/winner-stat-tile';
import { WinnerIdentityHeader } from './winner-identity-header';
import { WinnerStatusBadges } from './winner-status-badges';
interface TokenCtaIdentity {
  marketPubkey: string;
  winnerOutcomeIndex: number;
  /** Winning-outcome display name (already resolved to on-chain name). */
  name: string;
  /** Outcome ticker without a required `$` prefix. */
  ticker: string | null;
  imageUrl: string | null;
  imageNsfw: boolean;
}
export interface TokenCtaProps extends TokenCtaIdentity {
  /**
   * `pending` — market `resolved`, winning token still graduating to an AMM
   * token (skeleton stats + disabled "Graduating" button). `live` — market
   * `graduated`, token open for trading; the CTA links to `/winners/{mint}`.
   */
  variant: 'pending' | 'live';
  /** Winning-outcome SPL mint — required for the `live` variant link target. */
  mint?: string;
}

/**
 * Resolved-but-not-yet-graduated state — the winner is finalising into a
 * tradable token. Mirrors the graduated card's layout (brand "Ring" surface,
 * "Winning Outcome" status, outcome name, the 2+3 stat grid) but every value
 * is a zero placeholder until the AMM pool is live, and the CTA is a disabled
 * "Graduating" button with a spinner.
 */
function TokenCtaPending(props) {
  const $ = _c(62);
  const t = useTranslations("market.tokenCta");
  const {
    formatD6,
    formatBalance
  } = useFormattedCurrency();
  let t0;
  if ($[0] !== formatBalance) {
    t0 = formatBalance(0, "price");
    $[0] = formatBalance;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <span className="text-foreground-tertiary">{t0}</span>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const pendingPrice = t1;
  let t2;
  if ($[4] !== formatD6) {
    t2 = formatD6("0", "compact");
    $[4] = formatD6;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <span className="text-foreground-tertiary">{t2}</span>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const pendingMarketCap = t3;
  const t4 = props.marketPubkey;
  const t5 = props.winnerOutcomeIndex;
  const t6 = props.name;
  const t7 = props.imageUrl;
  const t8 = props.imageNsfw;
  let t9;
  if ($[8] !== t) {
    t9 = t("winningOutcome");
    $[8] = t;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t9) {
    t10 = <span className="text-caption text-foreground-secondary">{t9}</span>;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== props.imageNsfw || $[13] !== props.imageUrl || $[14] !== props.marketPubkey || $[15] !== props.name || $[16] !== props.winnerOutcomeIndex || $[17] !== t10) {
    t11 = <WinnerIdentityHeader marketPubkey={t4} outcomeIndex={t5} name={t6} imageUrl={t7} imageNsfw={t8} imageAlt="" status={t10} />;
    $[12] = props.imageNsfw;
    $[13] = props.imageUrl;
    $[14] = props.marketPubkey;
    $[15] = props.name;
    $[16] = props.winnerOutcomeIndex;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t) {
    t12 = t("priceLabel");
    $[19] = t;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== pendingPrice || $[22] !== t12) {
    t13 = <StatTile label={t12}>{pendingPrice}</StatTile>;
    $[21] = pendingPrice;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t) {
    t14 = t("marketCapLabel");
    $[24] = t;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== pendingMarketCap || $[27] !== t14) {
    t15 = <StatTile label={t14}>{pendingMarketCap}</StatTile>;
    $[26] = pendingMarketCap;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t13 || $[30] !== t15) {
    t16 = <div className="grid grid-cols-2 gap-2">{t13}{t15}</div>;
    $[29] = t13;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t) {
    t17 = t("change15m");
    $[32] = t;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <SignedBps bps={null} />;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t17) {
    t19 = <StatTile label={t17}>{t18}</StatTile>;
    $[35] = t17;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t) {
    t20 = t("change1h");
    $[37] = t;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <SignedBps bps={null} />;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t20) {
    t22 = <StatTile label={t20}>{t21}</StatTile>;
    $[40] = t20;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t) {
    t23 = t("change24h");
    $[42] = t;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <SignedBps bps={null} />;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t23) {
    t25 = <StatTile label={t23}>{t24}</StatTile>;
    $[45] = t23;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t19 || $[48] !== t22 || $[49] !== t25) {
    t26 = <div className="grid grid-cols-3 gap-2">{t19}{t22}{t25}</div>;
    $[47] = t19;
    $[48] = t22;
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== t16 || $[52] !== t26) {
    t27 = <div className="flex flex-col gap-2">{t16}{t26}</div>;
    $[51] = t16;
    $[52] = t26;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== t) {
    t28 = t("graduatingStatus");
    $[54] = t;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== t28) {
    t29 = <AnimatedButton variant="secondary" size="lg" className="w-full" disabled={true} loading={true}>{t28}</AnimatedButton>;
    $[56] = t28;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== t11 || $[59] !== t27 || $[60] !== t29) {
    t30 = <Panel tone="brand"><div className="flex flex-col gap-5 px-4">{t11}{t27}{t29}</div></Panel>;
    $[58] = t11;
    $[59] = t27;
    $[60] = t29;
    $[61] = t30;
  } else {
    t30 = $[61];
  }
  return t30;
}

/** Graduated state — token stats grid + a link into the `/winners` page. */
function TokenCtaLive(props) {
  const $ = _c(79);
  const t = useTranslations("market.tokenCta");
  const tMarket = useTranslations("market");
  const {
    formatD6,
    formatBalance
  } = useFormattedCurrency();
  let t0;
  if ($[0] !== props.marketPubkey) {
    t0 = {
      pubkeys: props.marketPubkey
    };
    $[0] = props.marketPubkey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const query = useGetWinners(t0);
  const liveRows = useLiveListData(query.queryKey, query.rows, query.entityQueryKey, query.projectEntityRow);
  const snapshot = liveRows[0] ?? query.rows[0];
  const mint = props.mint ?? snapshot?.mint ?? "";
  const price = snapshot?.price ?? "0";
  const marketCap = snapshot?.marketCap ?? "0";
  let t1;
  if ($[2] !== props.ticker) {
    t1 = <WinnerStatusBadges ticker={props.ticker} />;
    $[2] = props.ticker;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props.imageNsfw || $[5] !== props.imageUrl || $[6] !== props.marketPubkey || $[7] !== props.name || $[8] !== props.winnerOutcomeIndex || $[9] !== t1) {
    t2 = <WinnerIdentityHeader marketPubkey={props.marketPubkey} outcomeIndex={props.winnerOutcomeIndex} name={props.name} imageUrl={props.imageUrl} imageNsfw={props.imageNsfw} imageAlt="" status={t1} showSettledBadge={true} />;
    $[4] = props.imageNsfw;
    $[5] = props.imageUrl;
    $[6] = props.marketPubkey;
    $[7] = props.name;
    $[8] = props.winnerOutcomeIndex;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== t) {
    t3 = t("priceLabel");
    $[11] = t;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const t4 = Number(price);
  let t5;
  if ($[13] !== formatBalance) {
    t5 = n => formatBalance(n / D9_SCALE, "price");
    $[13] = formatBalance;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = <AnimatedNumber value={t4} format={t5} animateWithColour={true} duration={400} />;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t3 || $[19] !== t6) {
    t7 = <StatTile label={t3}>{t6}</StatTile>;
    $[18] = t3;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t) {
    t8 = t("marketCapLabel");
    $[21] = t;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const t9 = Number(marketCap);
  let t10;
  if ($[23] !== formatD6) {
    t10 = n_0 => formatD6(String(Math.round(n_0)), "compact");
    $[23] = formatD6;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t9) {
    t11 = <AnimatedNumber value={t9} format={t10} animateWithColour={true} duration={400} />;
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11 || $[29] !== t8) {
    t12 = <StatTile label={t8}>{t11}</StatTile>;
    $[28] = t11;
    $[29] = t8;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t12 || $[32] !== t7) {
    t13 = <div className="grid grid-cols-2 gap-2">{t7}{t12}</div>;
    $[31] = t12;
    $[32] = t7;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t) {
    t14 = t("change15m");
    $[34] = t;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const t15 = snapshot?.change15mBps ?? null;
  let t16;
  if ($[36] !== t15) {
    t16 = <SignedBps bps={t15} />;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t14 || $[39] !== t16) {
    t17 = <StatTile label={t14}>{t16}</StatTile>;
    $[38] = t14;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t) {
    t18 = t("change1h");
    $[41] = t;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const t19 = snapshot?.change1hBps ?? null;
  let t20;
  if ($[43] !== t19) {
    t20 = <SignedBps bps={t19} />;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t18 || $[46] !== t20) {
    t21 = <StatTile label={t18}>{t20}</StatTile>;
    $[45] = t18;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t) {
    t22 = t("change24h");
    $[48] = t;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  const t23 = snapshot?.change24hBps ?? null;
  let t24;
  if ($[50] !== t23) {
    t24 = <SignedBps bps={t23} />;
    $[50] = t23;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t22 || $[53] !== t24) {
    t25 = <StatTile label={t22}>{t24}</StatTile>;
    $[52] = t22;
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== t17 || $[56] !== t21 || $[57] !== t25) {
    t26 = <div className="grid grid-cols-3 gap-2">{t17}{t21}{t25}</div>;
    $[55] = t17;
    $[56] = t21;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t13 || $[60] !== t26) {
    t27 = <div className="flex flex-col gap-2">{t13}{t26}</div>;
    $[59] = t13;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  const t28 = `/winners/${mint}`;
  let t29;
  if ($[62] !== mint || $[63] !== props.marketPubkey || $[64] !== props.name || $[65] !== t28) {
    t29 = <ShareButton labeled={true} hideIcon={true} size="lg" className="flex-1" marketPubkey={props.marketPubkey} marketTitle={props.name} canonicalPath={t28} winnerMint={mint} />;
    $[62] = mint;
    $[63] = props.marketPubkey;
    $[64] = props.name;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  const t30 = `/winners/${mint}`;
  let t31;
  if ($[67] !== tMarket) {
    t31 = tMarket("trade.buy");
    $[67] = tMarket;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== t30 || $[70] !== t31) {
    t32 = <AnimatedButton asChild={true} variant="primary" size="lg" className="flex-1"><Link href={t30}>{t31}</Link></AnimatedButton>;
    $[69] = t30;
    $[70] = t31;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== t29 || $[73] !== t32) {
    t33 = <div className="flex gap-3">{t29}{t32}</div>;
    $[72] = t29;
    $[73] = t32;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] !== t2 || $[76] !== t27 || $[77] !== t33) {
    t34 = <Panel><div className="flex flex-col gap-5 px-4">{t2}{t27}{t33}</div></Panel>;
    $[75] = t2;
    $[76] = t27;
    $[77] = t33;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  return t34;
}

/**
 * Graduated/resolved replacement for the market-detail trade form. On
 * `/markets`, the right-column trade form is swapped for this card once a
 * market resolves: `pending` while the winning token is graduating, `live`
 * once it has graduated (a neutral stats card linking to `/winners`).
 */
export function TokenCta(t0) {
  const $ = _c(11);
  let identity;
  let mint;
  let variant;
  if ($[0] !== t0) {
    ({
      variant,
      mint,
      ...identity
    } = t0);
    $[0] = t0;
    $[1] = identity;
    $[2] = mint;
    $[3] = variant;
  } else {
    identity = $[1];
    mint = $[2];
    variant = $[3];
  }
  if (variant === "live") {
    let t1;
    if ($[4] !== mint) {
      t1 = mint !== undefined ? {
        mint
      } : {};
      $[4] = mint;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== identity || $[7] !== t1) {
      t2 = <TokenCtaLive {...identity} {...t1} />;
      $[6] = identity;
      $[7] = t1;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  let t1;
  if ($[9] !== identity) {
    t1 = <TokenCtaPending {...identity} />;
    $[9] = identity;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}